import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import api, { endpoints } from '../utils/apiClient';

const TournamentDetails = () => {
  const { id } = useParams();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchTournament();
  }, [id]);

  // Clear notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchTournament = async () => {
    try {
      console.log('Fetching tournament with ID:', id);
      // Fix: Use the correct endpoint (details instead of get)
      const data = await api.get(endpoints.tournaments.details(id));

      console.log('Tournament API response:', data);
      if (!data || !data._id) throw new Error('Tournament not found');

      setTournament(data);

      // Check if user is already registered
      if (user && data.participants) {
        const isRegistered = data.participants.some(p =>
          p.user?._id === user._id || p.user === user._id
        );
        setIsUserRegistered(isRegistered);
      }
    } catch (error) {
      setError(error.message);
      setNotification({
        type: 'error',
        message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTournament = async () => {
    if (!user) {
      setNotification({
        type: 'error',
        message: 'Please log in to register for the tournament'
      });
      return;
    }

    if (isUserRegistered) {
      setNotification({
        type: 'error',
        message: 'You are already registered for this tournament'
      });
      return;
    }

    if (tournament.participants?.length >= tournament.maxParticipants) {
      setNotification({
        type: 'error',
        message: 'Tournament is full'
      });
      return;
    }

    try {
      setIsRegistering(true);
      const prevParticipants = tournament.participants || [];

      await api.post(endpoints.tournaments.join(id));

      // Update states immediately in the correct order
      setIsUserRegistered(true);
      setTournament(prevTournament => {
        if (prevParticipants.some(p => p.user?._id === user._id)) {
          return prevTournament;
        }
        return {
          ...prevTournament,
          participants: [...prevParticipants, { user: user }]
        };
      });

      setNotification({
        type: 'success',
        message: 'Successfully registered for the tournament!'
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message
      });
    } finally {
      setIsRegistering(false);
    }
  };

  // Notification component
  const Notification = ({ type, message }) => (
    <div
      className={`fixed top-4 right-4 flex items-center gap-2 p-4 rounded-lg shadow-lg z-50 animate-fade-in ${
        type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}
    >
      {type === 'success' ? (
        <CheckCircleIcon className="w-5 h-5" />
      ) : (
        <ExclamationCircleIcon className="w-5 h-5" />
      )}
      {message}
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 text-white text-center">
        <div className="text-xl">Loading tournament details...</div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="p-6 text-white text-center">
        <div className="text-xl">Tournament not found</div>
      </div>
    );
  }

  const canRegister = user &&
    !isUserRegistered &&
    !isRegistering &&
    tournament.status === "Registration Open" &&
    tournament.participants?.length < tournament.maxParticipants;

  const handleClick = () => !user ? window.location.href = '/login' : handleJoinTournament();

  return (
    <div className="p-6 text-white">
      {/* Notification */}
      {notification && (
        <Notification type={notification.type} message={notification.message} />
      )}

      {/* Hero Section */}
      <div className="relative h-80 rounded-xl overflow-hidden mb-8">
        <img
          src={tournament.thumbnail || 'https://via.placeholder.com/1200x600?text=Tournament'}
          alt={tournament.title || 'Tournament'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/1200x600?text=Tournament';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
          <div className="p-8">
            <h1 className="text-4xl font-bold mb-4">{tournament.title || 'Tournament Details'}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className={`px-3 py-1 rounded-full ${
                tournament.status === "Registration Open"
                  ? "bg-green-500/20 text-green-400"
                  : "bg-red-500/20 text-red-400"
              }`}>
                {tournament.status || 'Unknown Status'}
              </span>
              <span className="text-gray-300">Game: {tournament.game || 'N/A'}</span>
              <span className="text-gray-300">
                Date: {tournament.date ? new Date(tournament.date).toLocaleDateString() : 'TBA'}
              </span>
              <span className="text-gray-300">
                Participants: {tournament.participants?.length || 0}/{tournament.maxParticipants || 'Unlimited'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Description */}
          <div className="bg-gray-900 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">About the Tournament</h2>
            <p className="text-gray-300">{tournament.description || 'No description available.'}</p>
          </div>

          {/* Rules */}
          <div className="bg-gray-900 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Tournament Rules</h2>
            {tournament.rules && tournament.rules.length > 0 ? (
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                {tournament.rules.map((rule, index) => (
                  <li key={index}>{rule}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-300">No specific rules have been set for this tournament.</p>
            )}
          </div>

          {/* Schedule */}
          <div className="bg-gray-900 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Tournament Schedule</h2>
            {tournament.schedule && tournament.schedule.length > 0 ? (
              <div className="space-y-4">
                {tournament.schedule.map((phase, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
                    <div>
                      <h3 className="font-semibold">{phase.phase || 'Phase ' + (index + 1)}</h3>
                      <p className="text-sm text-gray-400">
                        {phase.date ? new Date(phase.date).toLocaleDateString() : 'Date TBA'}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      phase.status === "Open"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-blue-500/20 text-blue-400"
                    }`}>
                      {phase.status || 'Upcoming'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-300">No schedule has been set for this tournament yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Prize Pool */}
          <div className="bg-gray-900 rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">Prize Pool</h2>
            <div className="text-2xl font-bold text-green-400 mb-6">{tournament.prize || 'TBA'}</div>
            {tournament.prizes && tournament.prizes.length > 0 ? (
              <div className="space-y-4">
                {tournament.prizes.map((prize, index) => (
                  <div key={index} className="p-4 bg-gray-800 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold">{prize.place || `${index + 1}${index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'}`} Place</span>
                      <span className="text-green-400">{prize.amount || 'TBA'}</span>
                    </div>
                    {prize.extras && <p className="text-sm text-gray-400">{prize.extras}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-300">Prize details will be announced soon.</p>
            )}
          </div>

          {/* Registration Button */}
          <button
            onClick={handleClick}
            disabled={isRegistering || isUserRegistered || (user && !canRegister)}
            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
              canRegister || !user
                ? "bg-purple-600 hover:bg-purple-700 transform hover:scale-[1.02]"
                : user ? "bg-gray-700 cursor-not-allowed opacity-75" : "bg-purple-600 hover:bg-purple-700 transform hover:scale-[1.02]"
            }`}
          >
            {isRegistering ? "Registering..." :
             !user ? "Login to Register" :
             isUserRegistered ? "Already Registered" :
             tournament.participants?.length >= tournament.maxParticipants ? "Tournament Full" :
             tournament.status !== "Registration Open" ? "Registration Closed" :
             "Register Now"
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentDetails;
